import { Flame, Inbox, Repeat2, ShieldCheck, Sparkles, type LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { Wordmark } from '@/components/brand/wordmark';
import { ThemeToggle } from '@/components/common/theme-toggle';

/** What the app does today (no promises about what is still planned). */
const POINTS: { icon: LucideIcon; title: string; text: string }[] = [
  {
    icon: Repeat2,
    title: 'Campaigns that follow up for you',
    text: 'Sequences, A/B versions and mailboxes that take turns.',
  },
  {
    icon: ShieldCheck,
    title: 'Every mailbox protected',
    text: 'Warm-up, DNS checks, health scores and safe sending limits.',
  },
  {
    icon: Inbox,
    title: 'Every reply in one place',
    text: 'Each reply goes straight to your team and your CRM.',
  },
];

/** An illustrative product preview (decorative, sample numbers): the panel's picture. */
function ProductPreview() {
  const bars = [38, 52, 46, 64, 58, 72, 80];
  return (
    <div
      aria-hidden="true"
      className="relative mt-8 pb-14 select-none [@media(max-height:879px)]:hidden"
    >
      <div className="rounded-2xl border border-white/15 bg-white/10 p-4 shadow-2xl backdrop-blur-sm">
        <div className="flex items-center justify-between text-[11px] text-brand-100">
          <span className="font-medium text-white">This week</span>
          <span>Sample data</span>
        </div>
        <div className="mt-3 grid grid-cols-3 gap-2">
          {[
            ['Emails sent', '1,284'],
            ['Replied', '96'],
            ['Opened', '412'],
          ].map(([label, value]) => (
            <div key={label} className="rounded-lg bg-white/10 px-2.5 py-2">
              <p className="text-[10px] text-brand-100">{label}</p>
              <p className="text-base font-semibold text-white tabular-nums">{value}</p>
            </div>
          ))}
        </div>
        <div className="mt-3 flex h-20 items-end gap-1.5 rounded-lg bg-white/5 px-2.5 pt-2 pb-2">
          {bars.map((h, i) => (
            <span
              key={i}
              className="flex-1 rounded-sm bg-gradient-to-t from-white/40 to-white/80"
              style={{ height: `${h}%` }}
            />
          ))}
        </div>
      </div>
      <div className="absolute -right-6 bottom-0 w-64 rounded-xl bg-white p-3 text-slate-900 shadow-2xl">
        <div className="flex items-center gap-2">
          <span className="grid size-7 place-items-center rounded-full bg-brand-100 text-[11px] font-semibold text-brand-700">
            RC
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-xs font-semibold">Rita Cole · Acme</p>
            <p className="truncate text-[11px] text-slate-500">
              “Sounds good, can we talk Tuesday?”
            </p>
          </div>
        </div>
        <p className="mt-2 inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
          <Sparkles className="size-3" /> New reply
        </p>
      </div>
      <div className="absolute -top-4 -left-3 inline-flex items-center gap-1.5 rounded-full bg-white px-2.5 py-1 text-[11px] font-medium text-slate-700 shadow-lg">
        <Flame className="size-3.5 text-amber-500" /> 3 mailboxes warming up
      </div>
    </div>
  );
}

/** Split layout for logged-out pages: brand panel on wide screens, form on the right. */
export function AuthLayout({
  title,
  description,
  children,
}: {
  title: string;
  description?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="grid min-h-dvh lg:h-dvh lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] lg:overflow-hidden">
      <aside className="relative hidden overflow-hidden bg-brand-700 px-12 py-10 text-white lg:flex lg:flex-col">
        <div
          aria-hidden="true"
          className="absolute inset-0 bg-[radial-gradient(circle_at_20%_15%,var(--color-brand-500),transparent_55%),radial-gradient(circle_at_85%_95%,var(--color-brand-950),transparent_55%)]"
        />
        <div
          aria-hidden="true"
          className="absolute inset-0 [background-image:linear-gradient(rgba(255,255,255,.06)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.06)_1px,transparent_1px)] [background-size:32px_32px] [mask-image:radial-gradient(ellipse_at_center,black,transparent_75%)]"
        />
        <div className="relative flex items-center gap-2">
          {/* The full logo's blue would vanish on this blue panel: mark on a white tile + name. */}
          <span className="grid size-9 place-items-center rounded-lg bg-white p-1 shadow-sm">
            <img src="/logo-mark.png" alt="" className="size-full" />
          </span>
          <span className="text-[15px] font-semibold tracking-tight">Tekansa Connect</span>
        </div>

        <div className="relative my-auto max-w-md py-6">
          <p className="text-xs font-semibold tracking-[0.18em] text-brand-200 uppercase">
            Email outreach platform
          </p>
          <p className="mt-3 text-2xl leading-tight font-semibold tracking-tight xl:text-3xl">
            Reach the inbox. Turn replies into customers.
          </p>
          <ul className="mt-6 grid gap-3.5">
            {POINTS.map(({ icon: Icon, title, text }) => (
              <li key={title} className="flex items-start gap-3">
                <span className="grid size-8 shrink-0 place-items-center rounded-lg bg-white/10 ring-1 ring-white/15">
                  <Icon aria-hidden="true" className="size-4 text-white" />
                </span>
                <span>
                  <span className="block text-sm font-semibold text-white">{title}</span>
                  <span className="block text-sm text-brand-100">{text}</span>
                </span>
              </li>
            ))}
          </ul>
          <ProductPreview />
        </div>

        <p className="relative text-xs text-brand-200">© Tekansa · tekansa.com</p>
      </aside>

      <main className="flex flex-col px-4 py-6 sm:px-8 lg:overflow-y-auto">
        <div className="flex items-center justify-between">
          <Wordmark className="lg:invisible" />
          <ThemeToggle />
        </div>
        <div className="mx-auto flex w-full max-w-sm flex-1 flex-col justify-center py-8">
          <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
          {description && <div className="mt-2 text-sm text-muted-foreground">{description}</div>}
          <div className="mt-8">{children}</div>
        </div>
      </main>
    </div>
  );
}
